import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Circle, ExternalLink, Globe, MapPin, Phone } from 'lucide-react';
import { FACILITY_LABELS, type ListingOverview } from '@his/shared';
import { del, get, session } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { EmptyState, PageHeader } from '@/components/ui/layout';
import { Alert, Badge, Button, PageSkeleton } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';

const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * How this facility appears on the public directory.
 *
 * Each branch is shown the way a patient will see it — the same card the
 * directory renders — next to a checklist of what still stands between it
 * and being found. Publishing is opt-in per branch and reversible in one
 * click: a staff clinic or a back office simply stays unlisted.
 */
export function PublicListingPage() {
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const manage = can('tenant.settings.manage');
  const facility = FACILITY_LABELS[user?.facilityType ?? 'hospital'].toLowerCase();
  const slug = session.tenant();

  const listings = useQuery({
    queryKey: ['directory-listings'],
    queryFn: () => get<ListingOverview>('/directory/listings'),
  });

  const unpublish = useMutation({
    mutationFn: (branchId: string) => del(`/directory/listings/${branchId}`),
    onSuccess: () => {
      toast.success('Removed from the directory');
      void queryClient.invalidateQueries({ queryKey: ['directory-listings'] });
    },
    onError: (error) => toast.error('Could not unpublish', describeError(error)),
  });

  const directoryOn = listings.data?.directoryEnabled ?? true;

  return (
    <>
      <PageHeader
        title="Public listing"
        description="What patients see when they search for you by city"
        actions={
          slug ? (
            <a
              href={`/find/${slug}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-8 items-center rounded-md border bg-background px-3 text-xs font-medium hover:bg-accent focus-ring"
            >
              <ExternalLink className="me-1.5 h-3.5 w-3.5" /> Open public page
            </a>
          ) : undefined
        }
      />

      {listings.data && !directoryOn ? (
        <Alert variant="warning" className="mb-4">
          <Globe className="me-2 inline h-4 w-4" />
          This {facility} is currently switched off in the public directory by the platform owner.
          You can still prepare your listings here, but none of them are visible to patients until
          the owner turns it back on.
        </Alert>
      ) : (
        <Alert className="mb-4">
          <Globe className="me-2 inline h-4 w-4" />
          A published branch appears in the public directory, and patients can ask for appointments,
          lab visits and medicines there. Nothing they send is booked until your own staff approve it.
        </Alert>
      )}

      {listings.isLoading && (
        <PageSkeleton />
      )}
      {listings.data && listings.data.branches.length === 0 && (
        <EmptyState title="No branches" description="Add a branch under Branches first; each one is listed separately." />
      )}

      <div className="space-y-4">
        {listings.data?.branches.map((row) => {
          const steps = [
            { done: Boolean(row.city), label: 'City set on the branch', hint: 'Patients search by city' },
            { done: row.types.length > 0, label: 'Listed as a hospital, lab or pharmacy' },
            { done: row.services.length > 0 || Boolean(row.about), label: 'Services or a short description' },
            { done: row.openingHours.length > 0, label: 'Opening hours' },
            { done: row.isPublished, label: 'Published' },
            { done: directoryOn, label: 'Directory switched on by the owner' },
          ];
          const live = row.isPublished && directoryOn && Boolean(row.city);
          return (
            <section key={row.branchId} className="grid gap-4 rounded-lg border bg-card p-4 lg:grid-cols-5">
              {/* Left: the card exactly as the directory draws it. */}
              <div className="lg:col-span-3">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Patients see
                </p>
                <div className={cn('rounded-lg border p-4', !live && 'opacity-60')}>
                  <p className="font-medium">{row.branchName}</p>
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" />
                    {[row.address, row.city].filter(Boolean).join(', ') || 'No address or city yet'}
                  </p>
                  {row.phone && (
                    <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Phone className="h-3.5 w-3.5" />
                      {row.phone}
                    </p>
                  )}
                  {row.about && <p className="mt-2 text-sm">{row.about}</p>}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {row.types.map((type) => (
                      <Badge key={type} variant="secondary">
                        {type}
                      </Badge>
                    ))}
                    {row.services.slice(0, 6).map((service) => (
                      <Badge key={service} variant="muted">
                        {service}
                      </Badge>
                    ))}
                    {row.types.length === 0 && row.services.length === 0 && (
                      <span className="text-xs text-muted-foreground">Nothing set up yet</span>
                    )}
                  </div>
                  {row.openingHours.length > 0 && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {row.openingHours
                        .map((hours) => `${WEEKDAYS_SHORT[hours.weekday]} ${hours.opensAt}–${hours.closesAt}`)
                        .join(' · ')}
                    </p>
                  )}
                </div>
              </div>

              {/* Right: status, what is missing, and the buttons. */}
              <div className="flex flex-col gap-3 lg:col-span-2">
                <div className="flex items-center justify-between">
                  <Badge variant={live ? 'success' : row.isPublished ? 'warning' : 'muted'}>
                    {live ? 'live in the directory' : row.isPublished ? 'published, not visible' : 'not listed'}
                  </Badge>
                  {live && slug && (
                    <Link
                      to={`/find/${slug}/${row.branchId}`}
                      target="_blank"
                      className="text-xs text-primary underline-offset-4 hover:underline"
                    >
                      View
                    </Link>
                  )}
                </div>
                <ul className="space-y-1 text-sm">
                  {steps.map((step) => (
                    <li key={step.label} className="flex items-start gap-2">
                      {step.done ? (
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                      ) : (
                        <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      )}
                      <span className={step.done ? '' : 'text-muted-foreground'}>
                        {step.label}
                        {!step.done && step.hint && <span className="block text-xs">{step.hint}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
                {manage && (
                  <div className="mt-auto flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => navigate(`/admin/listing/edit?branchId=${encodeURIComponent(row.branchId)}`)}>
                      {row.types.length ? 'Edit listing' : 'Set up listing'}
                    </Button>
                    {row.isPublished && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => unpublish.mutate(row.branchId)}
                        loading={unpublish.isPending}
                      >
                        Unpublish
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </>
  );
}
